import React from 'react'

//MUI
import Card from '@mui/material/Card'
import Box from '@mui/material/Box'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import RichText, { IRichText } from '../../../RichText'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import Divider from '@mui/material/Divider'

import { Link as GatsbyLink } from 'gatsby'
import useSanitizedSlug from '../../../../hooks/useSanitizedSlug'
import { HelpPageCategoryComponentProps } from '../HelpPageCard/HelpPageCard.types'

const HelpPageCategoryCard: React.FC<HelpPageCategoryComponentProps> = data => {
  const { body, articles, image } = data!
  const theme = useTheme()

  let columnLength = 1
  const isSmDevice = useMediaQuery(useTheme().breakpoints.down('md'))
  const isMdDevice = useMediaQuery(useTheme().breakpoints.between('md', 'lg'))
  const isLgDevice = useMediaQuery(useTheme().breakpoints.up('lg'))
  let columnWidth = '100%'
  if (isLgDevice) {
    columnLength = articles && articles.length > 8 ? 3 : articles && articles.length > 4 ? 2 : 1
    columnWidth = `calc(${columnLength} * 33.33%)`
  } else if (isMdDevice) {
    columnLength = articles && articles.length > 4 ? 2 : 1
    columnWidth = `calc(${columnLength} * 50%)`
  } else {
    columnLength = 1
  }

  const isColumnBreak = columnLength > 1 && articles && articles.length <= columnLength * 4 ? 1 : 0

  const styles = {
    root: {
      padding: '32px 24px 24px 24px',
      marginBottom: '16px',
      [theme.breakpoints.up('md')]: {
        display: 'flex',
      },
    },
    firstCard: {
      width: '300px',
      [theme.breakpoints.down('md')]: {
        width: '100%',
      },
      '& .MuiTypography-root': {
        marginTop: '10px',
        marginBottom: '-10px',
        marginRight: '30px',
        paddingBottom: '10px',
      },
    },
    imgStyle: {
      width: '60px',
      height: '60px',
    },
    listItem: {
      wordWrap: 'break-word',
      '&.MuiListItem-root': {
        margin: 0,
        padding: '3px 3px',
      },
      '& a': {
        color: theme.palette.common.black,
        textDecoration: 'none',
      },
      '&:nth-child(4n)': {
        breakAfter: isColumnBreak ? 'column' : '',
      },
    },
    listItemText: {
      '& .MuiTypography-displayBlock': {
        ...theme.typography.body1,
      },
    },
    divider: {
      margin: '10px',
    },
    rootBoxStyle: {
      width: '100%',
      paddingLeft: '20px',
      [theme.breakpoints.down('md')]: {
        paddingLeft: 0,
      },
    },
    ulStyle: {
      padding: 0,
      margin: 0,
      columnCount: columnLength,
      width: columnWidth,
      [theme.breakpoints.down('md')]: {
        '&.MuiBox-root': {
          paddingLeft: 0,
        },
      },
    },
  }
  return (
    <Card variant="outlined" sx={styles.root}>
      <Box sx={styles.firstCard}>
        {image?.file?.url && <img src={image?.file?.url} alt={image?.description || ''} style={styles.imgStyle} />}
        {body && <RichText {...(body as IRichText)} />}
      </Box>
      {isSmDevice ? (
        <Divider variant="middle" orientation="horizontal" flexItem sx={styles.divider} />
      ) : (
        <Divider variant="middle" orientation="vertical" flexItem />
      )}
      <Box sx={styles.rootBoxStyle}>
        <Box component="ul" sx={styles.ulStyle}>
          {articles &&
            articles.map((article, index: number) => {
              if (!article?.title) return
              const path =
                article?.internal?.type === 'HelpUniversePageGeneric'
                  ? article?.slug
                  : `${'backlink' in article && article.backlink?.slug ? article.backlink.slug : ''}/${article?.slug}`
              const sanitizedSlug = useSanitizedSlug(`/kundeservice/${path}`) // Generate sanitized slug for each article
              return (
                <ListItem key={index} sx={styles.listItem}>
                  <GatsbyLink to={`${sanitizedSlug}`}>
                    <ListItemText sx={styles.listItemText} primary={article?.title} />
                  </GatsbyLink>
                </ListItem>
              )
            })}
        </Box>
      </Box>
    </Card>
  )
}

export default HelpPageCategoryCard
